/**
 * Фича «Начертить стену».
 *
 * Поддерживает оба привычных способа:
 *   • клик — клик — клик: цепочка стен, каждая следующая продолжает предыдущую,
 *     Esc / правый клик / двойной клик — закончить;
 *   • нажал — протянул — отпустил: одна стена, после отпускания черновик сбрасывается
 *     и сразу можно начинать следующую в другом месте.
 * Различаем по смещению курсора между нажатием и отпусканием.
 *
 * Точный ввод: пока ведётся линия, можно просто начать печатать цифры —
 * появится поле длины, Enter построит стену ровно этой длины в текущем направлении
 * (почти ровное направление выравнивается по горизонтали, вертикали или 45°).
 * Это основной способ чертить точно, мышью задаётся только направление.
 *
 * Стена встаёт ровно туда, где её показал черновик: точку уже выбрала привязка курсора
 * (угол — в толщине его стен, стена — в её толщине), и при постройке её больше не тянет
 * к углу в полтора шага сетки (`DRAW_NODE_TOLERANCE`). Иначе начало, поставленное на стену
 * в 15 см от её конца, при клике прыгало в угол — и стена вставала наискось.
 *
 * Прямая стена важнее угла чуть в стороне (`snapPoint`): у свободного конца соседней стены
 * привязка просит дотянуть его до линии новой стены (`stretch`) — при постройке конец едет
 * вдоль своей оси (`applyStretch`), проёмы на той стене остаются на месте, и всё это — тот же
 * шаг истории, что и новая стена.
 *
 * Стена сквозь стену или поверх стены не строится: пока курсор ведёт линию
 * через существующую стену, черновик краснеет и рядом с курсором висит подсказка,
 * а клик ничего не ставит. Примыкание концом (Т-стык) при этом разрешено.
 *
 * Линия черчения — ось стены. Но если новая стена (любым способом) замкнула
 * отдельный контур, начерченная линия становится внутренней гранью комнаты: стены
 * отходят наружу на полтолщины (`inflateClosedContour`). Нарисовал 8 × 2 м — комната
 * в чистоте 8,00 × 2,00, а не 7,88 × 1,88. Черчение на этом заканчивается.
 *
 * Способ черчения (`wallMethod`, как «Геометрический вариант» в ArchiCAD):
 *   • цепочка — всё, что выше;
 *   • одна стена — две точки, и черчение начинается заново;
 *   • прямоугольник — клик в угол, клик в противоположный (или протянуть): четыре стены;
 *   • повёрнутый прямоугольник — первая стена под любым углом (клик — клик или протянуть),
 *     третий клик задаёт ширину.
 * Прямоугольник — это комната одним шагом истории, в чистоте по начерченному контуру. Он
 * раскладывается так же, как «Создать комнату» (`roomLayout`): у готовой стены прилипает к ней
 * и берёт её себе, вторую рядом не строит. Повёрнутый — четыре свои стены, отдельно стоящий
 * в чистоте по контуру. Размеры можно ввести с клавиатуры: у прямоугольника
 * «3000 2000», у повёрнутого — длину первой стены, потом ширину.
 *
 * Хук ничего не рисует: он отдаёт состояние черновика и обработчики,
 * которые виджет-холст навешивает на SVG.
 */
import { useEffect, useRef, useState } from "react";
import type { PointerEvent } from "react";
import {
	effectiveWallGraph,
	initialWallGraph,
	MIN_WALL_LENGTH,
	WALL_EDGE_TOLERANCE,
	findWallConflict,
	snapPoint,
	type SnapResult,
	type WallConflict,
} from "@/shared/plan";
import { SelectionKind, Tool, useEditorStore } from "@/widgets/editor";
import { inflateClosedContour } from "@/shared/store/actions/addWallAction";
import { useWallsStore } from "@/shared/store/layers/wallsStore";
import { addWallKeepingRooms } from "../roomCoverings";
import { useProjectStore } from "@/shared/store";
import {
	add,
	angleOf,
	dist,
	dot,
	fromLengthUnit,
	mul,
	norm,
	snapTo,
	sub,
	type Pt,
} from "@/shared/lib/geometry";
import { useLengthUnitStore } from "@/shared/store/lengthUnitStore";
import { parseSize, rotatedCorners, sidesOf, widthFrom } from "../wallShapes";
import { alignToWallLines, roomLayout } from "../roomLayout";
import type { RoomGhost } from "./usePlaceRoom";

/** Смещение курсора, начиная с которого нажатие считается протягиванием, px */
const DRAG_THRESHOLD_PX = 6;

/**
 * С каким углом слипается точка стены при постройке, мм. Только совсем рядом: где точка,
 * решила привязка курсора, а ближе `MIN_WALL_LENGTH` к углу стену всё равно не разрезать.
 */
const DRAW_NODE_TOLERANCE = MIN_WALL_LENGTH;

/** Число с клавиатуры — в единице редактора (мм, см, м, дюймы); стене нужны миллиметры. */
const typedMillimeters = (value: number) =>
	fromLengthUnit(value, useLengthUnitStore.getState().unit);

/** Сколько держать подсказку после отклонённого клика, мс */
const NOTICE_MS = 1800;

/** Текст подсказки для каждого вида конфликта. */
const CONFLICT_MESSAGE: Record<WallConflict["kind"], string> = {
	cross: "Стена не может проходить сквозь другую стену",
	overlap: "Стена не может лежать на другой стене",
};

interface Params {
	toWorld: (clientX: number, clientY: number) => Pt;
	mmPerPx: () => number;
}

/** Положение курсора внутри холста в пикселях — чтобы поставить рядом поле ввода. */
export interface ScreenPoint {
	x: number;
	y: number;
}

const TOO_SMALL_MESSAGE = "Комната слишком маленькая";
const BLOCKED_MESSAGE = "Комната задевает существующие стены";

/** Подсказки по шагам прямоугольников: что указать дальше. */
const STEP_HINT = {
	rectangleStart: "Укажите первый угол комнаты",
	rectangleEnd: "Укажите противоположный угол или введите размеры: 3000 2000",
	rotatedStart: "Укажите начало первой стены",
	rotatedSide: "Укажите конец первой стены или введите её длину",
	rotatedWidth: "Отведите курсор на ширину комнаты или введите её",
};

/** Первая стена повёрнутого прямоугольника — от неё откладывается ширина. */
interface Base {
	a: Pt;
	b: Pt;
}

/** Свободный конец соседней стены, который привязка просит дотянуть до новой стены */
type Stretch = SnapResult["stretch"];

/** Насколько, градусов, мышь может отклониться от ровного направления при точном вводе. */
const DIRECTION_SNAP_DEG = 12;

/**
 * Направление для точного ввода длины: близкое к горизонтали, вертикали или 45° —
 * ровно по ним. Длину задают цифрами, а направление мышью, и рука всегда чуть косит:
 * полградуса на десяти метрах — это 9 см вбок, квадрат 10 × 10 не сходится в углу,
 * и комната не замыкается. Явно косое направление (больше 12°) оставляем как есть.
 */
/** Восемь ровных направлений черчения — через каждые 45°, начиная с оси X. */
const EIGHT_DIRECTIONS: Pt[] = [
	{ x: 1, y: 0 },
	{ x: Math.SQRT1_2, y: Math.SQRT1_2 },
	{ x: 0, y: 1 },
	{ x: -Math.SQRT1_2, y: Math.SQRT1_2 },
	{ x: -1, y: 0 },
	{ x: -Math.SQRT1_2, y: -Math.SQRT1_2 },
	{ x: 0, y: -1 },
	{ x: Math.SQRT1_2, y: -Math.SQRT1_2 },
];

const snapDirection = (direction: Pt): Pt => {
	const angle = Math.atan2(direction.y, direction.x);
	const step = Math.PI / 4;
	const index = Math.round(angle / step);
	if (Math.abs(angle - index * step) > (DIRECTION_SNAP_DEG * Math.PI) / 180) return direction;
	// Точный вектор из таблицы: cos/sin дали бы хвосты вроде 6e-17, и стена ушла бы с оси
	return EIGHT_DIRECTIONS[(index + 8) % 8];
};

/**
 * Дотянуть свободный конец соседней стены туда, где встанет конец новой: он едет вдоль
 * своей оси, и стены сходятся чистым углом. Проёмы на этой стене остаются где были:
 * отступ проёма считается от начала стены, и если едет начало — отступ правим.
 */
const applyStretch = (stretch: NonNullable<Stretch>, to: Pt) => {
	const walls = useWallsStore.getState();
	const plan = walls.items;
	const node = plan.nodes[stretch.nodeId];
	const wall = Object.values(plan.walls).filter(
		(candidate) => candidate.a === stretch.nodeId || candidate.b === stretch.nodeId,
	);
	if (!node || wall.length !== 1) return;
	const [moving] = wall;
	const a = plan.nodes[moving.a];
	const b = plan.nodes[moving.b];
	if (!a || !b) return;
	const shift = moving.a === stretch.nodeId ? dot(sub(to, node), norm(sub(b, a))) : 0;
	const openings = Object.values(plan.openings).filter((o) => o.wallId === moving.id);
	walls.moveNode(stretch.nodeId, to.x, to.y);
	for (const opening of openings) walls.setOpeningOffset(opening.id, opening.offset - shift);
};

export const useDrawWall = ({ toWorld, mmPerPx }: Params) => {
	const tool = useEditorStore((s) => s.tool);
	const gridStep = useEditorStore((s) => s.gridStep);
	const underlayShown = useProjectStore(
		(s) => s.project.layers.underlay.visible && s.project.layers.underlay.image !== null,
	);
	const wallSettings = useEditorStore((s) => s.wallSettings);
	const method = useEditorStore((s) => s.wallMethod);
	const planStage = useEditorStore((s) => s.planStage);
	const drawResetToken = useEditorStore((s) => s.drawResetToken);
	const rawGraph = useProjectStore((s) => s.project.layers.walls);
	const graph =
		planStage === "initial" || planStage === "demolition"
			? initialWallGraph(rawGraph)
			: effectiveWallGraph(rawGraph);
	const isMountedStage =
		planStage !== "initial" && planStage !== "demolition" && planStage !== "all";

	// Точка старта живёт в ref: между двумя быстрыми кликами React может не успеть
	// перерисоваться, и обработчик увидел бы устаревшее значение из useState.
	const fromRef = useRef<Pt | null>(null);
	const [from, setFromState] = useState<Pt | null>(null);
	const [preview, setPreview] = useState<SnapResult | null>(null);
	const [screen, setScreen] = useState<ScreenPoint | null>(null);
	/** Где курсор на плане до привязки — с его стороны стены встают размеры от неё */
	const [pointer, setPointer] = useState<Pt | null>(null);
	const [typedLength, setTypedLength] = useState<string | null>(null);
	const [direction, setDirection] = useState<Pt>({ x: 1, y: 0 });
	/** Конфликт стены под курсором — черновик красный, клик отклоняется */
	const [conflict, setConflict] = useState<WallConflict | null>(null);
	/** Подсказка после отклонённой попытки: живёт недолго и гаснет сама */
	const [notice, setNotice] = useState<string | null>(null);
	/** Первая стена повёрнутого прямоугольника; в ref — по той же причине, что и `fromRef` */
	const baseRef = useRef<Base | null>(null);
	const [base, setBaseState] = useState<Base | null>(null);
	/** Углы повёрнутого прямоугольника под курсором — призрак комнаты */
	const [corners, setCorners] = useState<Pt[] | null>(null);
	/** Призрак прямоугольника — раскладка, как у «Создать комнату» */
	const [ghost, setGhost] = useState<RoomGhost | null>(null);
	/** В какую сторону от первой стены курсор — туда ляжет введённая с клавиатуры ширина */
	const widthSignRef = useRef(1);
	/** Последнее положение курсора — призрак комнаты после ввода длины с клавиатуры */
	const cursorRef = useRef<{ clientX: number; clientY: number; altKey: boolean } | null>(null);

	// Последнее направление — чтобы точный ввод работал, даже если мышь не двигали
	const directionRef = useRef<Pt>({ x: 1, y: 0 });
	const previewRef = useRef<SnapResult | null>(null);
	const typedLengthRef = useRef<string | null>(null);
	/**
	 * Стены текущей цепочки: откуда шла каждая и какая встала. Ctrl+Z и Backspace посреди
	 * цепочки снимают последнюю и ведут черновик снова из её начала; закончили цепочку —
	 * последняя стена выделяется, и лишнее правят полем «Длина», не выходя из инструмента.
	 */
	const chainRef = useRef<{ start: Pt; wallId: string | null }[]>([]);
	/** Где нажали кнопку — чтобы отличить клик от протягивания */
	const pressRef = useRef<{ x: number; y: number; hadOrigin: boolean } | null>(null);

	const active = tool === Tool.Wall;

	const setFrom = (point: Pt | null) => {
		fromRef.current = point;
		setFromState(point);
	};

	const setBase = (next: Base | null) => {
		baseRef.current = next;
		setBaseState(next);
	};

	/** Короче этого сторона комнаты быть не может: узлы её углов слиплись бы привязкой. */
	const minSide = Math.max(MIN_WALL_LENGTH * 2, gridStep * 2);

	const resolve = (
		e: { clientX: number; clientY: number; altKey: boolean },
		origin: Pt | null,
	): SnapResult => {
		const snap = snapPoint(graph, toWorld(e.clientX, e.clientY), {
			gridStep,
			nodeTolerance: 10 * mmPerPx(),
			from: origin,
			freeform: e.altKey,
			// Стены ловят конец всей толщиной: угол замыкается, перегородка встаёт в Т-стык
			wallBodies: true,
			// Обводят подложку — точка там, где кликнули: углы плана на картинке не на сетке
			grid: !underlayShown,
		});
		// Привязка видит стены этапа, а двигать конец можно, только если и в данных у него одна
		// стена: на «Монтаже» скрытая снесённая стена могла делить с ним узел — тогда просто угол
		const stretch = snap.stretch;
		if (stretch) {
			const owners = Object.values(rawGraph.walls).filter(
				(wall) => wall.a === stretch.nodeId || wall.b === stretch.nodeId,
			);
			if (owners.length !== 1) return { point: stretch.from, kind: "node", guides: {} };
		}
		return snap;
	};

	/**
	 * От какой точки привязка ведёт луч с шагом угла 15°. Только у стены: её ведут от начала.
	 * У прямоугольников луча нет — угол комнаты идёт за курсором свободно: луч тянул бы угол
	 * мимо курсора на ближайшие 15°, почти под первым углом схлопывал бы ширину в ноль, а первую
	 * стену повёрнутого не давал бы поставить под любым углом. Им — сетка, углы, стены
	 * и выравнивание по чужим углам; Alt — совсем без привязок.
	 */
	const aimFrom = (): Pt | null =>
		method === "rectangle" || method === "rotated" ? null : fromRef.current;

	/**
	 * Допуск слипания с углом у прямоугольников: их углы идут за курсором без привязки
	 * к углам, и замыкать их на соседний угол нужно щедрее. Стене — `DRAW_NODE_TOLERANCE`.
	 */
	const shapeNodeTolerance = gridStep * 1.5;

	/** Те же допуски, что и у построения стены в сторе, — иначе проверка разойдётся с результатом. */
	const wallOptions = (nodeTolerance = shapeNodeTolerance) => ({
		...wallSettings,
		mounted: isMountedStage,
		snapTolerance: nodeTolerance,
	});

	/** Пересечёт ли стена от origin до point существующую — по актуальному чертежу. */
	const check = (origin: Pt, point: Pt, nodeTolerance = shapeNodeTolerance): WallConflict | null =>
		findWallConflict(graph, origin, point, {
			thickness: wallSettings.thickness,
			nodeTolerance,
			edgeTolerance: WALL_EDGE_TOLERANCE,
		});

	/**
	 * Проверка стены цепочки или одиночной — с тем же допуском, с каким её построит `buildWall`.
	 * Дотягиваемый конец соседней стены (`stretch`) тоже не должен задеть другие стены.
	 */
	const checkWall = (origin: Pt, point: Pt, stretch?: Stretch): WallConflict | null => {
		const found = check(origin, point, DRAW_NODE_TOLERANCE);
		if (found || !stretch) return found;
		const moving = stretchedWall(stretch);
		if (!moving) return null;
		const { wall, other } = moving;
		const rest = {
			...graph,
			walls: Object.fromEntries(Object.entries(graph.walls).filter(([id]) => id !== wall.id)),
		};
		return findWallConflict(rest, other, point, {
			thickness: wall.thickness,
			nodeTolerance: DRAW_NODE_TOLERANCE,
			edgeTolerance: WALL_EDGE_TOLERANCE,
		});
	};

	/** Стена, чей свободный конец дотягивают, и её другой конец; null — конец уже не свободный. */
	const stretchedWall = (stretch: NonNullable<Stretch>) => {
		const walls = Object.values(graph.walls).filter(
			(wall) => wall.a === stretch.nodeId || wall.b === stretch.nodeId,
		);
		if (walls.length !== 1) return null;
		const wall = walls[0];
		const other = graph.nodes[wall.a === stretch.nodeId ? wall.b : wall.a];
		return other ? { wall, other } : null;
	};

	/** Первая стена комнаты, которая задела бы существующие, — или null. */
	const checkCorners = (points: Pt[]): WallConflict | null => {
		for (let i = 0; i < points.length; i++) {
			const found = check(points[i], points[(i + 1) % points.length]);
			if (found) return found;
		}
		return null;
	};

	/** Ширина повёрнутого прямоугольника под курсором: до угла или стены, иначе — с шагом сетки. */
	const widthAt = (e: { clientX: number; clientY: number; altKey: boolean }, along: Base) => {
		const snap = resolve(e, null);
		if (snap.kind === "node" || snap.kind === "wall")
			return widthFrom(along.a, along.b, snap.point);
		const width = widthFrom(along.a, along.b, toWorld(e.clientX, e.clientY));
		return e.altKey || underlayShown ? width : snapTo(width, gridStep);
	};

	/** Углы прямоугольника, который сейчас построился бы; null — не тот способ или шаг. */
	const cornersAt = (e: { clientX: number; clientY: number; altKey: boolean }): Pt[] | null => {
		const origin = fromRef.current;
		if (!origin) return null;
		const along = baseRef.current;
		if (method === "rotated" && along) return rotatedCorners(along.a, along.b, widthAt(e, along));
		return null;
	};

	/**
	 * Комната по двум углам: начерченный прямоугольник — её чистый контур, стены встают снаружи.
	 * Раскладка — как у «Создать комнату»: рядом с готовой стеной сторона прилипает к её грани
	 * и становится этой стеной (`roomLayout`).
	 */
	const rectangleGhost = (a: Pt, b: Pt, keepSize = false): RoomGhost => {
		const plan = useProjectStore.getState().project.layers.walls;
		const { thickness } = wallSettings;
		const box = alignToWallLines(
			plan,
			{
				top: Math.min(a.y, b.y),
				bottom: Math.max(a.y, b.y),
				left: Math.min(a.x, b.x),
				right: Math.max(a.x, b.x),
			},
			thickness,
			keepSize,
		);
		const width = box.right - box.left;
		const depth = box.bottom - box.top;
		const center = { x: (box.left + box.right) / 2, y: (box.top + box.bottom) / 2 };
		const layout = roomLayout(plan, center, width, depth, thickness);
		// Допуск угла — как у постройки ниже: стены встают точно, и угол чужой стены в паре
		// сантиметров — это наложение, а не стык
		const blocked = layout.segments.some(
			(segment) =>
				findWallConflict(plan, segment.from, segment.to, {
					thickness: segment.thickness,
					nodeTolerance: 1,
					edgeTolerance: WALL_EDGE_TOLERANCE,
				}) !== null,
		);
		const { inner, segments } = layout;
		return { inner, segments, width, depth, bearing: wallSettings.bearing, blocked };
	};

	/** Поставить комнату по двум углам, если можно. Одна правка — один шаг истории. true — поставили. */
	const commitRectangle = (a: Pt, b: Pt, keepSize = false): boolean => {
		const room = rectangleGhost(a, b, keepSize);
		if (room.width < minSide || room.depth < minSide) {
			setNotice(TOO_SMALL_MESSAGE);
			return false;
		}
		if (room.blocked) {
			setGhost(room);
			setNotice(BLOCKED_MESSAGE);
			return false;
		}
		useProjectStore.getState().recordEdit(() => {
			for (const segment of room.segments) {
				// Стены — ровно туда, куда их положила раскладка: привязка к углу в полутора шагах
				// сетки потянула бы конец к соседнему углу, и стена встала бы косо
				addWallKeepingRooms(segment.from, segment.to, {
					...wallSettings,
					mounted: isMountedStage,
					thickness: segment.thickness,
					snapTolerance: 1,
				});
			}
		});
		cancel();
		return true;
	};

	/** Показывает подсказку о конфликте и гасит её через паузу. */
	const reject = (found: WallConflict) => {
		setConflict(found);
		setNotice(CONFLICT_MESSAGE[found.kind]);
	};

	useEffect(() => {
		if (notice === null) return;
		const timer = window.setTimeout(() => setNotice(null), NOTICE_MS);
		return () => window.clearTimeout(timer);
	}, [notice]);

	const cancel = () => {
		chainRef.current = [];
		setFrom(null);
		setBase(null);
		setCorners(null);
		setGhost(null);
		setPreview(null);
		previewRef.current = null;
		setTypedLength(null);
		typedLengthRef.current = null;
		setConflict(null);
	};

	/** Стена только что встала от `start` до узла `endNodeId` — запоминаем её в цепочке. */
	const remember = (start: Pt, endNodeId: string | null) => {
		// Стена не встала — шага истории нет, и снимать в цепочке нечего
		if (!endNodeId) return;
		const walls = useProjectStore.getState().project.layers.walls;
		const wall = Object.values(walls.walls).find((w) => {
			const a = walls.nodes[w.a];
			return w.b === endNodeId && a !== undefined && dist(a, start) < 1;
		});
		chainRef.current = [...chainRef.current, { start, wallId: wall?.id ?? null }];
	};

	/**
	 * Закончить черчение (правый клик, двойной клик, одна стена построена): последняя стена
	 * выделяется — справа её длина, перебор правят сразу, инструмент «Стена» остаётся.
	 */
	const finish = () => {
		const last = chainRef.current.at(-1)?.wallId ?? null;
		cancel();
		if (last && useProjectStore.getState().project.layers.walls.walls[last]) {
			useEditorStore.getState().setSelection({ kind: SelectionKind.Wall, id: last });
		}
	};

	/**
	 * Шаг назад посреди черчения (Ctrl+Z, Backspace): последняя стена цепочки снимается,
	 * черновик снова ведут из её начала. Стен ещё нет — сбрасывается точка начала.
	 * @returns true — шаг обработан черчением, общая отмена не нужна
	 */
	const stepBack = (): boolean => {
		if (!active || !fromRef.current) return false;
		if (baseRef.current) {
			// Повёрнутый прямоугольник: сначала забываем первую сторону
			setFrom(baseRef.current.a);
			setBase(null);
			setCorners(null);
			return true;
		}
		const last = chainRef.current.at(-1);
		if (!last) {
			cancel();
			return true;
		}
		useProjectStore.getState().undo();
		chainRef.current = chainRef.current.slice(0, -1);
		setFrom(last.start);
		setPreview(null);
		previewRef.current = null;
		setTypedLength(null);
		typedLengthRef.current = null;
		setConflict(null);
		return true;
	};

	// Выбрали инструмент кнопкой или клавишей — незаконченная цепочка забывается:
	// новая стена начнётся там, где пользователь кликнет, а не из старой точки
	useEffect(() => {
		cancel();
		// cancel пересоздаётся каждый рендер, но реагировать нужно только на счётчик
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [drawResetToken]);

	/**
	 * Построить стену. Замкнула отдельный контур — как бы его ни чертили: цепочкой кликов,
	 * протягиванием по одной стене, точным вводом — начерченная линия становится
	 * внутренней гранью комнаты (`inflateClosedContour`). Это одна правка: её и записывает
	 * история одним шагом.
	 * @returns конечный узел и замкнулся ли контур
	 */
	const buildWall = (start: Pt, end: Pt, stretch?: Stretch) => {
		if (stretch) applyStretch(stretch, end);
		const endNodeId = addWallKeepingRooms(start, end, wallOptions(DRAW_NODE_TOLERANCE));
		const closed = endNodeId !== null && inflateClosedContour(endNodeId);
		return { endNodeId, closed };
	};

	/**
	 * Построить комнату-прямоугольник: стены от угла к углу, последняя замыкается на первый.
	 * Отдельно стоящая комната отходит наружу на полтолщины — контур становится чистым.
	 */
	const buildRoom = (points: Pt[]) => {
		let start = points[0];
		let endNodeId: string | null = null;
		for (let i = 0; i < points.length; i++) {
			const end = points[(i + 1) % points.length];
			endNodeId = addWallKeepingRooms(start, end, wallOptions());
			// Следующая стена — от узла, где встала эта: привязка могла его сдвинуть
			const node = endNodeId
				? useProjectStore.getState().project.layers.walls.nodes[endNodeId]
				: null;
			start = node ? { x: node.x, y: node.y } : end;
		}
		if (endNodeId) inflateClosedContour(endNodeId);
	};

	/**
	 * Поставить комнату по углам, если можно: стороны не короче `minSide` и ни одна стена не
	 * задевает существующие. Четыре стены — один шаг истории. true — построили.
	 */
	const commitCorners = (points: Pt[]): boolean => {
		const [width, depth] = sidesOf(points);
		if (width < minSide || depth < minSide) {
			setNotice(TOO_SMALL_MESSAGE);
			return false;
		}
		const found = checkCorners(points);
		if (found) {
			reject(found);
			return false;
		}
		useProjectStore.getState().recordEdit(() => buildRoom(points));
		cancel();
		return true;
	};

	/** Первая стена повёрнутого прямоугольника задана — дальше ширина. */
	const commitBase = (a: Pt, b: Pt, e?: { clientX: number; clientY: number; altKey: boolean }) => {
		if (dist(a, b) < minSide) {
			setNotice(TOO_SMALL_MESSAGE);
			return;
		}
		const found = check(a, b);
		if (found) {
			reject(found);
			return;
		}
		const next = { a, b };
		setBase(next);
		setConflict(null);
		// Призрак комнаты — сразу под курсором (ввели длину с клавиатуры — под последним его местом)
		const at = e ?? cursorRef.current;
		setCorners(at ? rotatedCorners(a, b, widthAt(at, next)) : null);
	};

	/** Стена построена: контур замкнулся — черчение заканчивается, нет — цепочка идёт дальше. */
	const continueOrFinish = (
		start: Pt,
		point: Pt,
		built: { endNodeId: string | null; closed: boolean },
	) => {
		// Замкнули комнату — готово; править тут нечего, контур раздвинулся в чистоту
		if (built.closed) {
			cancel();
			return;
		}
		remember(start, built.endNodeId);
		// Одна стена — и всё: следующая начнётся там, где кликнут, а эта выделена
		if (method === "single") {
			finish();
			return;
		}
		continueFrom(point, built.endNodeId);
	};

	/** Продолжает цепочку от только что построенной точки. */
	const continueFrom = (fallback: Pt, endNodeId: string | null) => {
		const node = endNodeId ? graph.nodes[endNodeId] : null;
		setFrom(node ? { x: node.x, y: node.y } : fallback);
		setConflict(null);
	};

	/** @returns true, если событие обработано фичей */
	const handlePointerDown = (e: PointerEvent<SVGSVGElement>): boolean => {
		if (!active || e.button !== 0) return false;
		setTypedLength(null);
		const origin = fromRef.current;
		pressRef.current = { x: e.clientX, y: e.clientY, hadOrigin: origin !== null };
		const snap = resolve(e, aimFrom());
		if (!origin) {
			// Новая стена — выделение прошлой (после «Закончить») снимается
			if (useEditorStore.getState().selection) useEditorStore.getState().setSelection(null);
			setFrom(snap.point);
			setNotice(null);
			return true;
		}
		if (method === "rectangle") {
			commitRectangle(origin, snap.point);
			return true;
		}
		if (method === "rotated") {
			const along = baseRef.current;
			if (along) commitCorners(rotatedCorners(along.a, along.b, widthAt(e, along)));
			else commitBase(origin, snap.point, e);
			return true;
		}
		// Стена прошла бы сквозь другую или легла на неё — клик не строит ничего,
		// черновик остаётся, чтобы можно было довести линию до правильного места
		const found = checkWall(origin, snap.point, snap.stretch);
		if (found) {
			reject(found);
			return true;
		}
		// Каждая стена — свой шаг истории: Ctrl+Z убирает последнюю, а не всю цепочку
		const endNodeId = useProjectStore
			.getState()
			.recordEdit(() => buildWall(origin, snap.point, snap.stretch));
		continueOrFinish(origin, snap.point, endNodeId);
		return true;
	};

	/** @returns true, если событие обработано фичей */
	const handlePointerMove = (e: PointerEvent<SVGSVGElement>): boolean => {
		if (!active) {
			if (preview) {
				setPreview(null);
				previewRef.current = null;
			}
			return false;
		}
		const snap = resolve(e, aimFrom());
		setPreview(snap);
		previewRef.current = snap;
		cursorRef.current = { clientX: e.clientX, clientY: e.clientY, altKey: e.altKey };
		setPointer(toWorld(e.clientX, e.clientY));

		const origin = fromRef.current;
		if (origin) {
			// Направление для точного ввода — от самого курсора, а не от точки, прилипшей
			// к сетке: рядом с началом стены сетка искажает угол на десятки градусов
			const vector = sub(toWorld(e.clientX, e.clientY), origin);
			if (Math.abs(vector.x) > 1e-6 || Math.abs(vector.y) > 1e-6) {
				const next = norm(vector);
				directionRef.current = next;
				setDirection(next);
			}
		}
		const along = baseRef.current;
		if (along) {
			const width = widthFrom(along.a, along.b, toWorld(e.clientX, e.clientY));
			if (width !== 0) widthSignRef.current = Math.sign(width);
		}
		const shape = cornersAt(e);
		setCorners(shape);
		const room = method === "rectangle" && origin ? rectangleGhost(origin, snap.point) : null;
		setGhost(room);
		if (room) {
			setConflict(null);
			if (!room.blocked) setNotice(null);
		} else if (shape) {
			const found = checkCorners(shape);
			setConflict(found);
			if (!found) setNotice(null);
		} else if (origin) {
			// Первая стена повёрнутого прямоугольника — со своим допуском, стена — со своим
			const found =
				method === "rotated"
					? check(origin, snap.point)
					: checkWall(origin, snap.point, snap.stretch);
			setConflict(found);
			// Линию довели до допустимого места — подсказка об отказе больше не нужна
			if (!found) setNotice(null);
		}

		const rect = e.currentTarget.getBoundingClientRect();
		setScreen({ x: e.clientX - rect.left, y: e.clientY - rect.top });
		return true;
	};

	/**
	 * Отпускание кнопки. Если курсор заметно сместился с момента нажатия —
	 * это протягивание: ставим стену и заканчиваем, не продолжая цепочку.
	 * @returns true, если событие обработано фичей
	 */
	const handlePointerUp = (e: PointerEvent<SVGSVGElement>): boolean => {
		const press = pressRef.current;
		pressRef.current = null;
		// Стилус или палец оторвали — наведения больше нет, и метка привязки с размерами
		// осталась бы висеть на последнем углу: непонятно, где привязка сейчас. Гасим;
		// стилус с наведением (Apple Pencil) покажет её снова, как только поднесут
		if (e.pointerType !== "mouse") {
			setPreview(null);
			previewRef.current = null;
		}
		if (!active || !press || press.hadOrigin) return false;

		const moved = Math.hypot(e.clientX - press.x, e.clientY - press.y);
		if (moved < DRAG_THRESHOLD_PX) return false; // это был клик — работаем цепочкой

		const origin = fromRef.current;
		if (!origin) return false;
		const snap = resolve(e, aimFrom());
		// Протянули по диагонали — комната; протянули первую стену — дальше ширина
		if (method === "rectangle") {
			if (!commitRectangle(origin, snap.point)) cancel();
			return true;
		}
		if (method === "rotated") {
			commitBase(origin, snap.point, e);
			if (!baseRef.current) cancel();
			return true;
		}
		const found = checkWall(origin, snap.point, snap.stretch);
		if (found) {
			// Протянутая стена отклонена: подсказку показываем, черновик сбрасываем как обычно
			cancel();
			reject(found);
			return true;
		}
		// Протянутая стена тоже может замкнуть комнату — и то и другое одним шагом истории
		const built = useProjectStore
			.getState()
			.recordEdit(() => buildWall(origin, snap.point, snap.stretch));
		if (built.closed) cancel();
		else {
			remember(origin, built.endNodeId);
			finish();
		}
		return true;
	};

	/** Размеры прямоугольника с клавиатуры: углы ложатся в ту четверть, где курсор. */
	const commitTypedSize = (origin: Pt, text: string) => {
		const typed = parseSize(text);
		if (!typed) return;
		const size = typed.map(typedMillimeters);
		const heading = directionRef.current;
		const to = {
			x: origin.x + size[0] * (heading.x < 0 ? -1 : 1),
			y: origin.y + size[1] * (heading.y < 0 ? -1 : 1),
		};
		// Введённый размер — ровно такой: выравнивание по соседям только сдвигает комнату
		commitRectangle(origin, to, true);
	};

	/** Строит стену заданной длины в текущем направлении. */
	const commitTypedLength = () => {
		const origin = fromRef.current;
		if (!origin || typedLength === null) return;
		const text = typedLength;
		if (method === "rectangle") {
			setTypedLength(null);
			typedLengthRef.current = null;
			commitTypedSize(origin, text);
			return;
		}
		const along = baseRef.current;
		if (method === "rotated" && along) {
			setTypedLength(null);
			typedLengthRef.current = null;
			const width = typedMillimeters(Number(text.replace(",", ".")));
			if (Number.isFinite(width)) {
				commitCorners(rotatedCorners(along.a, along.b, width * widthSignRef.current));
			}
			return;
		}
		const value = typedMillimeters(Number(typedLength.replace(",", ".")));
		if (!Number.isFinite(value) || value < 50) {
			setTypedLength(null);
			typedLengthRef.current = null;
			return;
		}
		const heading = snapDirection(directionRef.current);
		const target = add(origin, mul(heading, value));
		setTypedLength(null);
		typedLengthRef.current = null;
		setPreview(null);
		previewRef.current = null;
		if (method === "rotated") {
			commitBase(origin, target);
			return;
		}
		const found = checkWall(origin, target);
		if (found) {
			reject(found);
			return;
		}
		const endNodeId = useProjectStore.getState().recordEdit(() => buildWall(origin, target));
		continueOrFinish(origin, target, endNodeId);
	};

	// Начали печатать цифру — открываем поле точного ввода
	useEffect(() => {
		if (!active) return;
		const onKey = (e: KeyboardEvent) => {
			const target = e.target as HTMLElement | null;
			if (target && ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName)) return;
			if (!fromRef.current || typedLengthRef.current !== null) return;
			if (e.ctrlKey || e.metaKey || e.altKey) return;
			if (!/^[0-9]$/.test(e.key)) return;
			e.preventDefault();
			typedLengthRef.current = e.key;
			setTypedLength(e.key);
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [active]);

	const angle = preview && from ? angleOf(sub(preview.point, from)) : angleOf(direction);

	/** Текст подсказки у курсора: свежий отказ или живой конфликт под курсором. */
	const problem =
		notice ??
		(ghost?.blocked ? BLOCKED_MESSAGE : from && conflict ? CONFLICT_MESSAGE[conflict.kind] : null);
	/** У прямоугольников — что указать дальше, пока ничего не мешает. */
	const step =
		method === "rectangle"
			? from
				? STEP_HINT.rectangleEnd
				: STEP_HINT.rectangleStart
			: method === "rotated"
				? base
					? STEP_HINT.rotatedWidth
					: from
						? STEP_HINT.rotatedSide
						: STEP_HINT.rotatedStart
				: null;
	const hint = problem ?? step;
	const shapeSides = corners ? sidesOf(corners) : null;

	return {
		active,
		from,
		preview,
		pointer,
		screen,
		angle,
		typedLength,
		/** Стена под курсором сейчас недопустима — ось черновика пунктиром */
		invalid: from !== null && conflict !== null,
		/** Чем рисуем: несущая красная, перегородка синяя; толщина — в размер будущей стены */
		bearing: wallSettings.bearing,
		thickness: wallSettings.thickness,
		hint,
		/** Подсказка — ошибка (красная) или следующий шаг (синяя) */
		hintTone: problem ? ("error" as const) : ("info" as const),
		/** Призрак прямоугольника — как у «Создать комнату» */
		ghost,
		/** Призрак повёрнутого прямоугольника: углы по контуру и длины двух соседних сторон */
		room: corners && shapeSides ? { corners, width: shapeSides[0], depth: shapeSides[1] } : null,
		/** Вводят размеры прямоугольника — «ширина длина», а не одну длину */
		typedPair: method === "rectangle",
		setTypedLength,
		commitTypedLength,
		handlePointerDown,
		handlePointerMove,
		handlePointerUp,
		cancel,
		finish,
		stepBack,
	};
};
